import { Link } from "react-router-dom";

const SECTIONS = [
  {
    title: "1. Introduction",
    items: [
      "TumaTrack Technologies (\"TumaTrack\", \"we\", \"us\") operates a delivery management platform for Kenyan businesses. This Privacy Policy explains what personal data we collect, why we collect it, and the rights you have over it.",
      "This Policy is written to comply with the Kenya Data Protection Act, 2019 and applies to Shop Owners, Riders, and Customers who interact with the Platform.",
      "By using TumaTrack, you consent to the data practices described in this Policy. If you do not agree, please do not use the Platform.",
    ]
  },
  {
    title: "2. Data We Collect",
    content: [
      { term: "Business Data", def: "business name, address, KRA PIN, business registration number, and owner contact details, collected at registration." },
      { term: "Account Data", def: "name, phone number, email address, and password (stored as a secure hash)." },
      { term: "Order Data", def: "customer names, phone numbers, delivery addresses, order fees, and delivery status entered by Shop Owners." },
      { term: "Rider Location Data", def: "GPS coordinates recorded approximately every 30 seconds while a Rider is on an active delivery." },
      { term: "Payment Data", def: "M-Pesa transaction references and subscription payment status. TumaTrack does not store M-Pesa PINs or full financial account details." },
      { term: "Usage Data", def: "log-in activity, device and browser information, and error reports collected automatically via Sentry." },
    ]
  },
  {
    title: "3. How We Use Your Data",
    items: [
      "To create and manage Shop Owner, Rider, and Customer accounts.",
      "To process and track deliveries, including sharing live Rider location with the relevant Shop Owner and Customer via a tracking link.",
      "To send OTPs, order notifications, and account alerts via SMS (Africa's Talking) and email (Resend).",
      "To process subscription payments via M-Pesa STK Push.",
      "To monitor and fix technical errors via Sentry.",
      "To comply with legal obligations under Kenyan law.",
      "We do not sell personal data to third parties, and we do not use Order Data or Rider Location Data for advertising.",
    ]
  },
  {
    title: "4. Third-Party Processors",
    items: [
      "Africa's Talking — SMS delivery for OTPs and notifications.",
      "Resend — transactional email delivery.",
      "Safaricom M-Pesa — payment processing for subscriptions.",
      "Render — hosting for the TumaTrack API and database (PostgreSQL).",
      "Vercel — hosting for the TumaTrack web dashboard and website.",
      "Firebase — distribution of the Tuma Rider mobile application.",
      "Sentry — error monitoring across the Platform.",
      "Each processor accesses only the data necessary to perform its function and is bound by its own data protection obligations.",
    ]
  },
  {
    title: "5. Data Retention",
    items: [
      "Rider location data is retained for 90 days from the date it is recorded, then permanently deleted.",
      "Order history is retained for 90 days after account termination, after which it is permanently deleted unless you request an export.",
      "Account and business data is retained for as long as your account remains active, and for a reasonable period afterward to comply with tax and legal record-keeping obligations under Kenyan law.",
    ]
  },
  {
    title: "6. Your Rights",
    items: [
      "You have the right to access the personal data TumaTrack holds about you.",
      "You have the right to request correction of inaccurate or incomplete data.",
      "You have the right to request deletion of your account and associated data. Requests are processed within 30 days.",
      "Riders may withdraw consent to location tracking at any time by ceasing to accept active deliveries; note that this will prevent the Rider from fulfilling delivery duties on the Platform.",
      "To exercise any of these rights, contact us using the details below.",
    ]
  },
  {
    title: "7. Data Security",
    items: [
      "All data in transit is encrypted using TLS.",
      "Authentication is managed via tokenized sessions (Laravel Sanctum) with expiring tokens.",
      "Access to business data is scoped so that no Shop Owner can view another Shop Owner's data.",
      "While we take reasonable technical and organizational measures to protect your data, no system can guarantee absolute security.",
    ]
  },
  {
    title: "8. Children's Privacy",
    items: [
      "TumaTrack is not directed at, and does not knowingly collect data from, individuals under the age of 18. Shop Owners and Riders must be at least 18 years of age to register.",
    ]
  },
  {
    title: "9. Changes to This Policy",
    items: [
      "We may update this Privacy Policy from time to time. Material changes will be communicated via email or SMS to active Shop Owners. Continued use of the Platform after such changes constitutes acceptance of the updated Policy.",
    ]
  },
  {
    title: "10. Contact Us",
    items: [
      "If you have questions about this Privacy Policy or wish to exercise your data rights, contact us at support@tumatrack.co.ke or through our contact form.",
    ]
  },
];

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-slate-50" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&family=DM+Sans:wght@400;500;600&display=swap');`}</style>

      {/* Navbar */}
      <nav className="bg-slate-900 px-5 h-14 flex items-center justify-between sticky top-0 z-40">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-7 h-7 bg-orange-500 rounded-lg flex items-center justify-center">
            <svg viewBox="0 0 24 24" fill="none" className="w-4 h-4 text-white" stroke="currentColor" strokeWidth={2.2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 18.75a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 0 1-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 0 0-3.213-9.193 2.056 2.056 0 0 0-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 0 0-10.026 0 1.106 1.106 0 0 0-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12" />
            </svg>
          </div>
          <span className="text-white font-bold text-sm" style={{ fontFamily: "'Sora', sans-serif" }}>
            Tuma<span className="text-orange-400">Track</span>
          </span>
        </Link>
      </nav>

      <div className="max-w-3xl mx-auto px-4 py-12">

        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-3xl font-bold text-gray-900 mb-2" style={{ fontFamily: "'Sora', sans-serif" }}>
            Privacy Policy
          </h1>
          <p className="text-gray-500 text-sm">Effective Date: July 2026 · Governed by the Kenya Data Protection Act, 2019</p>
        </div>

        {/* Intro */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-6">
          <p className="text-gray-600 text-sm leading-relaxed">
            This Privacy Policy explains how TumaTrack Technologies collects, uses, and protects personal data
            when you use the TumaTrack platform, whether as a Shop Owner, Rider, or Customer. Please read it
            alongside our{" "}
            <Link to="/terms" className="text-orange-500 hover:text-orange-600 font-medium">
              Terms and Conditions
            </Link>.
          </p>
        </div>

        {/* Sections */}
        <div className="space-y-4">
          {SECTIONS.map((section) => (
            <div key={section.title} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
              <h2 className="font-bold text-gray-900 mb-4" style={{ fontFamily: "'Sora', sans-serif" }}>
                {section.title}
              </h2>
              {section.content ? (
                <div className="space-y-3">
                  {section.content.map((row) => (
                    <div key={row.term} className="flex gap-2 text-sm">
                      <span className="font-semibold text-gray-900 shrink-0">{row.term}</span>
                      <span className="text-gray-500">— {row.def}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <ul className="space-y-2">
                  {section.items.map((item, i) => (
                    <li key={i} className="flex gap-3 text-sm text-gray-600">
                      <span className="text-orange-400 font-bold shrink-0 mt-0.5">·</span>
                      {item}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>

        {/* Contact */}
        <div className="mt-6 bg-slate-900 rounded-2xl p-6 text-center">
          <p className="text-white font-bold mb-1" style={{ fontFamily: "'Sora', sans-serif" }}>Questions about your data?</p>
          <p className="text-slate-400 text-sm">
            Email us at{" "}
            <a href="mailto:support@tumatrack.co.ke" className="text-orange-400 hover:text-orange-300">
              support@tumatrack.co.ke
            </a>
            {" "}or use the{" "}
            <Link to="/contact" className="text-orange-400 hover:text-orange-300">
              contact form
            </Link>
          </p>
          <Link to="/register" className="inline-flex mt-4 bg-orange-500 hover:bg-orange-600 text-white font-semibold px-6 py-2.5 rounded-xl text-sm transition-colors">
            Back to Registration
          </Link>
        </div>
      </div>
    </div>
  );
}
